import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useParams } from 'react-router-dom';
import Breadcrumb from '../../../component/common/Breadcrumb';
import { getWorkoutById } from '../../../api/healthAPI';
import { errorToast, getLocalStorageItem, formatExerciseDuration } from '../../../utils/helper';
import {
  useWorkoutCreation,
  WorkoutCreationProvider
} from '../../../context/WorkoutCreationContext';

const formatWorkoutData = (data) => {
  if (!data) return null;

  const {
    _id,
    workout_name = '',
    workout_overview = '',
    location = '',
    difficulty = '',
    muscle_groups = [],
    is_active = true,
    thumbnail_url = '',
    cover_image_url = '',
    category_ids = [],
    equipments = [],
    stretches = [],
    exercises = []
  } = data;

  const mapExerciseLike = (list, type) =>
    list.map((item, index) => {
      const exercise = item?.exercise_id || {};
      const exerciseId = exercise._id || exercise.id || item.exercise_id || item._id || item.id;
      return {
        exercise_id: exerciseId,
        sequence: item.sequence || index + 1,
        metadata: item.metadata || { sets: [] },
        name: exercise.name || '',
        muscle_groups: exercise.muscle_groups || [],
        category_ids: exercise.category_ids || [],
        exercise_duration: exercise.exercise_duration || '',
        rest_time: exercise.rest_time || 10,
        video_url: exercise.video_url || '',
        image_url: exercise.image_url || '',
        difficulty: exercise.difficulty || '',
        type
      };
    });

  return {
    workoutId: _id,
    workout_name,
    workout_overview,
    location,
    difficulty,
    muscle_groups,
    category_names: category_ids
      .map((cat) => (typeof cat === 'object' ? cat?.name : cat))
      .filter(Boolean),
    is_active,
    thumbnail_url,
    cover_image_url,
    equipments: equipments.map((eq) => ({
      equipment_id: eq?.equipment_id?._id || eq?.equipment_id || eq?._id,
      name: eq?.equipment_id?.name || eq?.name || '',
      equipment_reason: eq?.equipment_reason || '',
      is_required: eq?.is_required !== undefined ? eq.is_required : true
    })),
    stretches: mapExerciseLike(stretches, 'stretch'),
    exercises: mapExerciseLike(exercises, 'exercise')
  };
};

const ViewWorkout = ({ isPreview = false, onExitPreview, onPublish }) => {
  const location = useLocation();
  const { id } = useParams();
  const workoutId = id;
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  // Always call the hook unconditionally (Rules of Hooks)
  // We'll only use the values when isPreview is true
  const { workoutData: contextWorkoutData, updateWorkoutData } = useWorkoutCreation();

  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);

  const breadcrumbPages = useMemo(
    () => [
      {
        name: 'Health Dashboard',
        href: `${userData?.slug ? `/${userData.slug}` : ''}/health/dashboard`
      },
      {
        name: 'Workouts',
        href: `${userData?.slug ? `/${userData.slug}` : ''}/health/workouts`
      },
      { name: 'Workout Preview', current: true }
    ],
    [userData?.slug]
  );

  useEffect(() => {
    const fetchWorkout = async () => {
      // If in preview mode and no workoutId, use context data
      if (isPreview && !workoutId) {
        console.log('Using context workout data for preview:', contextWorkoutData);
        setWorkout(contextWorkoutData);
        setLoading(false);
        return;
      }

      if (!workoutId) {
        setLoading(false);
        return;
      }

      try {
        const response = await getWorkoutById(workoutId);
        if (response?.meta?.code === 1 && response?.data) {
          const formattedData = formatWorkoutData(response.data);
          setWorkout(formattedData);
          // Update context if available (for edit mode)
          if (updateWorkoutData) {
            updateWorkoutData({
              ...response.data,
              workoutId: response.data._id
            });
          }
        } else {
          errorToast(response?.meta?.message || 'Failed to fetch workout details');
        }
      } catch (err) {
        console.error('Error fetching workout details:', err);
        errorToast('Failed to fetch workout details');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkout();
  }, [workoutId, isPreview]);

  const renderSectionTitle = (title) => (
    <h3 className="text-lg font-semibold text-[#0B1D2E] mb-4">{title}</h3>
  );

  const renderEquipment = () => {
    const equipments = workout?.equipments || [];
    if (!equipments.length) return <p className="text-sm text-gray-500">No equipment listed.</p>;

    return (
      <div className="grid gap-4">
        {equipments.map((item) => {
          const equipmentName = item?.name;
          const reason = item?.equipment_reason;
          const isRequired = item?.is_required;
          return (
            <div key={item?.equipment_id} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Equipment Name
                </label>
                <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE] flex items-center gap-2">
                  <span className="text-sm font-medium text-[#0B1D2E]">
                    {equipmentName || 'Unknown Equipment'}
                  </span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                      isRequired ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {isRequired ? 'Required' : 'Optional'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Equipment Reason
                </label>
                <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE]">
                  <span className="text-sm text-[#0B1D2E]">{reason || 'No reason specified'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExerciseCard = (data, index) => {
    const name = data?.name || data?.exercise_id?.name;
    const muscleGroups = data?.muscle_groups?.length
      ? data.muscle_groups
      : data?.exercise_id?.muscle_groups || [];
    const thumbnail = data?.image_url || data?.exercise_id?.image_url;
    const duration = data?.exercise_duration || data?.exercise_id?.exercise_duration;
    return (
      <div
        key={`${data?.exercise_id || index}-${data?.sequence || index}`}
        className="flex items-center gap-4 p-4 bg-[#F1F1F1] border border-[#E4E9EE] rounded-3xl"
      >
        <div className="w-[72px] h-[72px] rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={name || 'Exercise thumbnail'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
              No Image
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[#0B1D2E]">
            {name || 'Untitled Exercise'}
          </span>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
            {muscleGroups.length > 0 ? (
              <span>{muscleGroups.join(', ')}</span>
            ) : (
              <span>No muscle groups</span>
            )}
            {duration ? (
              <span className="flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                {formatExerciseDuration(duration)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderStretches = () => {
    const stretches = workout?.stretches || [];
    if (!stretches.length) return <p className="text-sm text-gray-500">No stretches added.</p>;

    return (
      <div className="grid gap-4">
        {stretches.map((stretch, index) => renderExerciseCard(stretch, index))}
      </div>
    );
  };

  const renderExercises = () => {
    const exercises = workout?.exercises || [];
    if (!exercises.length) return <p className="text-sm text-gray-500">No exercises added.</p>;

    return (
      <div className="grid gap-4">
        {exercises.map((exercise, index) => renderExerciseCard(exercise, index))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <title>Workout Preview | Shoorah Admin</title>
        </Helmet>
        <Breadcrumb pageList={breadcrumbPages} className="lg:mt-8" />
        <div className="bg-white rounded-2xl p-10 mt-6 shadow-sm text-center">
          <h2 className="text-xl font-semibold text-[#0B1D2E] mb-2">Workout not found</h2>
          <p className="text-sm text-gray-500 mb-6">
            The workout you are trying to view does not exist or has been removed.
          </p>
          <Link
            to={`${userData?.slug ? `/${userData.slug}` : ''}/health/workouts`}
            className="inline-flex items-center px-6 py-2 bg-shoorah-active text-white rounded-full text-sm"
          >
            Back to Workouts
          </Link>
        </div>
      </div>
    );
  }

  const thumbnail = workout?.thumbnail_url || workout?.cover_image_url;

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <title>{`${workout?.workout_name || 'Workout'} | Preview`} | Shoorah Admin</title>
      </Helmet>

      <Breadcrumb pageList={breadcrumbPages} className="lg:mt-8" />

      <div className="mt-6 flex flex-col gap-6">
        {/* Top row: Thumbnail and Edit Button - Transparent Background */}
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="w-[280px] flex-shrink-0">
            <div className="rounded-3xl overflow-hidden bg-gray-100 shadow-sm aspect-[4/3]">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={workout?.workout_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
                  No Thumbnail
                </div>
              )}
            </div>
          </div>
          {!isPreview && (
            <Link
              to={`${userData?.slug ? `/${userData.slug}` : ''}/health/workout/edit?id=${
                workout?.workoutId
              }`}
              state={{ _id: workout?.workoutId, action: 'edit' }}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-white bg-shoorah-active rounded-full shadow-sm hover:opacity-90 transition"
            >
              Edit Workout
            </Link>
          )}
        </div>

        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-[#ECEFF3]">
          {/* Workout Details Section */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-[#0B1D2E] mb-4">Workout Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Workout Title
                </label>
                <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE]">
                  <p className="text-sm text-[#0B1D2E]">{workout?.workout_name || 'Untitled'}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">
                    Workout Category
                  </label>
                  <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE]">
                    <p className="text-sm text-[#0B1D2E]">
                      {workout?.category_names?.length
                        ? workout.category_names.join(', ')
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Difficulty</label>
                  <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE]">
                    <p className="text-sm text-[#0B1D2E]">
                      {workout?.difficulty || 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workout Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Workout Description
            </label>
            <div className="px-4 py-3 bg-[#F1F1F1] rounded-xl border border-[#E4E9EE] min-h-[80px]">
              <p className="text-sm text-[#0B1D2E] leading-relaxed">
                {workout?.workout_overview || 'No description provided.'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-[#ECEFF3]">
          {renderSectionTitle('Equipment')}
          {renderEquipment()}
        </div>

        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-[#ECEFF3]">
          {renderSectionTitle('Stretches')}
          {renderStretches()}
        </div>

        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-[#ECEFF3]">
          {renderSectionTitle('Exercises')}
          {renderExercises()}
        </div>

        {/* Preview Mode Action Buttons */}
        {isPreview && (
          <div className="flex justify-end gap-4 mt-6">
            {onExitPreview && (
              <button
                type="button"
                onClick={onExitPreview}
                className="px-8 py-2 border border-gray-300 bg-white text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Back to Editing
              </button>
            )}
            {onPublish && (
              <button
                type="button"
                onClick={onPublish}
                className="btn-primary"
              >
                Publish
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
// Wrap the component with the WorkoutCreationProvider
const Viewworkout = ({ isPreview = false, onExitPreview, onPublish }) => {
  // When used in preview mode from NewWorkout, it will use the parent's context
  // When used standalone (direct route), we need to provide context
  if (isPreview) {
    return <ViewWorkout isPreview={isPreview} onExitPreview={onExitPreview} onPublish={onPublish} />;
  }

  return (
    <WorkoutCreationProvider>
      <ViewWorkout isPreview={isPreview} onExitPreview={onExitPreview} onPublish={onPublish} />
    </WorkoutCreationProvider>
  );
};
export default Viewworkout;
